import React, { useState } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Divider,
  IconButton,
  ListItemIcon,
  Avatar,
  Menu,
  MenuItem,
  Link,
  Chip,
  Button,
  Popover,
} from '@mui/material';
import { Logo } from '@horwath-htl/shared-branding';
import {
  Menu as MenuIcon,
  Logout,
  ManageAccounts,
  AdminPanelSettings as AdminIcon,
  DeveloperMode as DeveloperIcon,
  Login as LoginIcon,
  Receipt as QuickBooksIcon,
  Support as SupportIcon,
  Assessment as AppraisalIcon,
  Hub as HubIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { useLocation, Outlet } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useQueryClient } from '@tanstack/react-query';
import { useCurrentUser, type UserInfo } from '../hooks/useApi';
import { useAppNavigation } from '../hooks/useAppNavigation';
import { useAuthenticatedFetch } from '../hooks/useAuthenticatedFetch';
import { RouteKeyWrapper } from './routing/RouteKeyWrapper';
import { CookieConsent } from './CookieConsent';
import { brandColors } from '../theme';
import { entitlementsFetchManager } from '../services/entitlements-fetch-manager';
// import { useMessageNotifications } from '../hooks/useMessagePolling'; // Removed unread-count polling
import { API_URL } from '../utils/config';
import { clearEnvironmentStorage, getCurrentEnvironment } from '../utils/environment';
import { performCompleteMsalCleanup } from '../utils/msalCleanup';

export const Layout: React.FC = () => {
  const { navigateTo } = useAppNavigation();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { instance, accounts } = useMsal();
  const { data: currentUser } = useCurrentUser() as { data: UserInfo | null | undefined };
  const { authenticatedFetch } = useAuthenticatedFetch();
  const isAuthenticated = accounts.length > 0;
  // const { unreadCount } = useMessageNotifications(); // Removed unread-count polling

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);
  const [rolePermissionsAnchor, setRolePermissionsAnchor] = useState<null | HTMLElement>(null);
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [rolePermissions, setRolePermissions] = useState<string[]>([]);
  const [loadingPermissions, setLoadingPermissions] = useState(false);


  // Create main navigation items - adapted for private-web
  const navigationItems = [
    ...(currentUser?.permissions?.includes('store.view')
      ? [{ text: 'MarketCompass Reports', path: 'https://htlmarketcompass.com', isExternal: true }]
      : []),
    ...(currentUser?.permissions?.includes('accounting.view')
      ? [{ text: 'Subsidiary Accounting', path: '/admin/hub/accounting/my-vendors', isExternal: false }]
      : []),
    ...((currentUser?.permissions?.includes('deliverables.all') || currentUser?.permissions?.includes('deliverables.view'))
      ? [{ text: 'Deliverables', path: '/deliverables', isExternal: false }]
      : []),
    ...(currentUser?.permissions?.includes('market_compass.customize')
      ? [{ text: 'Manage Your Markets', path: '/admin/hub/marketcompass/my-markets', isExternal: false }]
      : []),
  ];

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleRoleClick = async (event: React.MouseEvent<HTMLElement>, roleName: string) => {
    setRolePermissionsAnchor(event.currentTarget);
    setSelectedRole(roleName);
    setLoadingPermissions(true);
    
    try {
      // Fetch actual permissions for this role from the API
      const response = await authenticatedFetch(`${API_URL}/api/portal-admin/roles`);
      
      if (response && response.ok) {
        const data = await response.json();
        const role = data.data?.find((r: any) => r.name === roleName);
        if (role?.permissions) {
          const formattedPermissions = role.permissions.map((p: any) => p.id);
          setRolePermissions(formattedPermissions);
        } else {
          setRolePermissions(['No permissions found for this role']);
        }
      } else {
        setRolePermissions(['Failed to load permissions']);
      }
    } catch (error) {
      console.error('Error fetching role permissions:', error);
      setRolePermissions(['Error loading permissions']);
    } finally {
      setLoadingPermissions(false);
    }
  };

  const handleRolePermissionsClose = () => {
    setRolePermissionsAnchor(null);
    setSelectedRole(null);
  };

  const handleLogout = async () => {
    const currentEnv = getCurrentEnvironment();
    console.log(`Logging out from ${currentEnv.displayName} environment...`);
    
    // Clear ALL React Query cache data
    queryClient.clear();
    
    // Clear entitlements cache
    entitlementsFetchManager.clearCache();
    
    // Use comprehensive MSAL cleanup utility
    await performCompleteMsalCleanup(instance);
    
    // Clear environment-specific localStorage data
    clearEnvironmentStorage();
    
    // Logout from Azure AD
    instance.logoutRedirect({
      postLogoutRedirectUri: '/',
    });
  };

  const handleNavigateToAdmin = (path: string) => {
    navigateTo(path);
    handleProfileMenuClose();
  };



  const currentAccount = accounts[0];
  const displayName = currentUser?.email?.split('@')[0] || currentAccount?.name || 'User';
  const displayEmail = currentUser?.email || currentAccount?.username || '';
  const isFullBleedPage =
    location.pathname === '/' ||
    location.pathname === '/dashboard' ||
    location.pathname === '/global-market-reports' ||
    location.pathname === '/market-pulse' ||
    location.pathname.startsWith('/admin/hub');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', height: '100%' }}>
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: 'rgba(255,255,255,0.85)',
          backdropFilter: 'saturate(180%) blur(10px)',
          color: brandColors.indigo,
          boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
          borderBottom: '1px solid rgba(17,35,55,0.08)',
        }}
      >
        <Toolbar sx={{ gap: 2, minHeight: 72 }}>
          {/* Logo - clickable to go home */}
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mr: 3, 
              cursor: 'pointer',
              '& *': { cursor: 'pointer' },
            }}
            onClick={() => navigateTo('/')}
          >
            <Logo height={35} />
          </Box>

          {/* Desktop Navigation */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 2, flexGrow: 1 }}>
            {navigationItems.map((item) => {
              const isActive = !item.isExternal && location.pathname === item.path;
              return (
                <Button
                  key={item.text}
                  onClick={() => item.isExternal ? window.open(item.path, '_blank') : navigateTo(item.path)}
                  sx={{
                    textTransform: 'none',
                    color: brandColors.indigo,
                    backgroundColor: 'transparent',
                    fontWeight: 500,
                    letterSpacing: '0.3px',
                    fontSize: '0.95rem',
                    py: 1,
                    borderRadius: 0,
                    borderBottom: isActive ? `3px solid ${brandColors.gold}` : '3px solid transparent',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: brandColors.gold,
                      borderBottomColor: brandColors.gold,
                    },
                  }}
                >
                  {item.text}
                </Button>
              );
            })}
          </Box>

          {/* Mobile Navigation Button */}
          <IconButton
            color="inherit"
            aria-label="open menu"
            edge="start"
            onClick={handleMobileMenuOpen}
            sx={{ display: { xs: 'flex', md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          {/* Spacer for mobile */}
          <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isAuthenticated ? (
              <IconButton
                size="large"
                edge="end"
                aria-label="account of current user"
                aria-controls="profile-menu"
                aria-haspopup="true"
                onClick={handleProfileMenuOpen}
                color="inherit"
              >
                <Avatar sx={{ width: 32, height: 32 }}>
                  {displayName.charAt(0).toUpperCase()}
                </Avatar>
              </IconButton>
            ) : (
              <Button
                variant="text"
                startIcon={<LoginIcon />}
                onClick={() => navigateTo('/login', { state: { from: location.pathname + location.search } })}
                sx={{
                  color: brandColors.indigo,
                  '&:hover': {
                    backgroundColor: 'transparent',
                    color: brandColors.gold,
                  },
                  display: { xs: 'none', md: 'flex' }, // Hide on mobile, show on desktop
                }}
              >
                Login
              </Button>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Menu */}
      <Menu
        anchorEl={mobileMenuAnchor}
        open={Boolean(mobileMenuAnchor)}
        onClose={handleMobileMenuClose}
        PaperProps={{
          sx: { width: '100%', maxWidth: 360 }
        }}
      >
        {navigationItems.map((item) => {
          const isSelected = !item.isExternal && location.pathname === item.path;
          return (
            <MenuItem
              key={item.text}
              onClick={() => {
                if (item.isExternal) {
                  window.open(item.path, '_blank');
                } else {
                  navigateTo(item.path);
                }
                handleMobileMenuClose();
              }}
              selected={isSelected}
            >
              {item.text}
            </MenuItem>
          );
        })}
        
        {/* Login button for mobile - only show when not authenticated */}
        {!isAuthenticated && [
          <Divider key="login-divider" />,
          <MenuItem
            key="login-item"
            onClick={() => {
              navigateTo('/login', { state: { from: location.pathname + location.search } });
              handleMobileMenuClose();
            }}
          >
            <ListItemIcon>
              <LoginIcon />
            </ListItemIcon>
            Login
          </MenuItem>
        ]}
      </Menu>

      {/* Profile Menu - only render when authenticated */}
      {isAuthenticated && (
        <Menu
          id="profile-menu"
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleProfileMenuClose}
          onClick={handleProfileMenuClose}
          PaperProps={{
            sx: { minWidth: 250 }
          }}
        >
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              {displayName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {displayEmail}
            </Typography>
            {currentUser?.roles && currentUser.roles.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                {currentUser.roles.map((role) => (
                  <Chip 
                    key={role.id}
                    label={role.name} 
                    size="small" 
                    color="primary"
                    variant="outlined"
                    onClick={(e) => handleRoleClick(e, role.name)}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      }
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>

          {/* Barrett Email Assistant - available to all authenticated users */}
          <Divider />
          <MenuItem onClick={() => handleNavigateToAdmin('/email-assistant')}>
            <ListItemIcon>
              <EmailIcon fontSize="small" />
            </ListItemIcon>
            Barrett Email Assistant
          </MenuItem>

          {/* Admin Hub - Single entry point for all admin functions */}
          {(currentUser?.permissions?.includes('market_compass.admin') ||
            currentUser?.permissions?.includes('accounting.admin') ||
            currentUser?.permissions?.includes('deliverables.all') ||
            currentUser?.permissions?.includes('messages.all') ||
            currentUser?.permissions?.includes('permissions.all') ||
            currentUser?.permissions?.includes('developer_tools.view')) && [
            <Divider key="admin-divider" />,
            <MenuItem key="admin-hub" onClick={() => handleNavigateToAdmin('/admin/hub')}>
              <ListItemIcon>
                <HubIcon fontSize="small" />
              </ListItemIcon>
              Admin Hub
            </MenuItem>,
            currentUser?.permissions?.includes('developer_tools.view') && (
              <MenuItem key="dev-tools" onClick={() => handleNavigateToAdmin('/admin/hub/developer')}>
                <ListItemIcon>
                  <DeveloperIcon fontSize="small" />
                </ListItemIcon>
                Developer Tools
              </MenuItem>
            )
          ]}

          <Divider />
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <Logout fontSize="small" />
            </ListItemIcon>
            Logout
          </MenuItem>
        </Menu>
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: isFullBleedPage ? 0 : 3,
          mt: 8, // Space for the fixed AppBar
          width: '100%',
        }}
      >
        <RouteKeyWrapper>
          <Outlet />
        </RouteKeyWrapper>
      </Box>
      
      <Box
        component="footer"
        sx={{
          backgroundColor: '#011E41', // brandColors.indigo
          color: 'white',
          py: 2, // Much thinner padding
          px: 3,
          mt: 'auto',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography variant="body2">
            © Copyright Barrett HTL Inc {new Date().getFullYear()} - Horwath HTL US Licensee Portal
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Link
              href="/privacy"
              color="inherit"
              underline="hover"
              onClick={(e) => {
                e.preventDefault();
                navigateTo('/privacy');
              }}
              sx={{
                fontSize: '0.875rem',
                cursor: 'pointer',
                '&:hover': {
                  color: '#F5A800', // brandColors.gold
                },
              }}
            >
              Privacy Policy
            </Link>
            
            <Link
              href="/eula"
              color="inherit"
              underline="hover"
              onClick={(e) => {
                e.preventDefault();
                navigateTo('/eula');
              }}
              sx={{
                fontSize: '0.875rem',
                cursor: 'pointer',
                '&:hover': {
                  color: '#F5A800', // brandColors.gold
                },
              }}
            >
              EULA
            </Link>
          </Box>
        </Box>
      </Box>
      
      {/* Role Permissions Popover */}
      <Popover
        open={Boolean(rolePermissionsAnchor)}
        anchorEl={rolePermissionsAnchor}
        onClose={handleRolePermissionsClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, minWidth: 300 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
            {selectedRole} Permissions:
          </Typography>
          <Divider sx={{ mb: 1.5 }} />
          {loadingPermissions ? (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Loading permissions...
            </Typography>
          ) : (
            rolePermissions.map((permission, index) => (
              <Typography key={index} variant="body2" sx={{ mb: 0.75 }}>
                • {permission}
              </Typography>
            ))
          )}
        </Box>
      </Popover>
      
      {/* Cookie Consent Banner */}
      <CookieConsent />
    </Box>
  );
};
